import React, { useState, useEffect, useCallback, useMemo } from "react";
import VideoBox from "./VideoBox";
import { FaStar, FaRegStar } from "react-icons/fa";
import { pickProximityPair, updateEloRatings } from "./services";
import { Link } from "./usePath";

const MEDALS = ["🥇", "🥈", "🥉"];

const TOGGLES = [
  { key: "all", label: "All", division: null },
  { key: "m", label: "Men's", division: "M" },
  { key: "w", label: "Women's", division: "W" },
];

function SchoolPage({ schoolName, db, allVideos, loading, refreshVideos, onVote }) {
  const [activeToggle, setActiveToggle] = useState("all");
  const [leftVideo, setLeftVideo] = useState(null);
  const [rightVideo, setRightVideo] = useState(null);
  const [voting, setVoting] = useState(false);

  const schoolVideos = useMemo(
    () => allVideos.filter((v) => v.school === schoolName),
    [allVideos, schoolName]
  );

  const stats = useMemo(() => {
    const winners = schoolVideos.filter((v) => v.winner).length;
    const finalists = schoolVideos.filter((v) => v.finalist || v.winner).length;
    const nominees = schoolVideos.length;
    return { winners, finalists, nominees };
  }, [schoolVideos]);

  const filtered = useMemo(() => {
    const t = TOGGLES.find((x) => x.key === activeToggle);
    return schoolVideos.filter((v) =>
      t.division ? v.division === t.division : true
    );
  }, [schoolVideos, activeToggle]);

  const ranked = useMemo(
    () => [...filtered].sort((a, b) => (b.elo || 1000) - (a.elo || 1000)),
    [filtered]
  );

  const pickPair = useCallback(() => {
    const [l, r] = pickProximityPair(filtered);
    setLeftVideo(l);
    setRightVideo(r);
  }, [filtered]);

  useEffect(() => {
    pickPair();
  }, [pickPair]);

  useEffect(() => {
    const prev = document.title;
    document.title = `${schoolName} — callahanvideos`;
    return () => {
      document.title = prev;
    };
  }, [schoolName]);

  const handleVote = async (winnerId, loserId) => {
    if (voting) return;
    setVoting(true);
    try {
      await updateEloRatings(winnerId, loserId, db);
      await refreshVideos();
      onVote && onVote();
    } catch (e) {
      console.error("Vote failed:", e);
    }
    setVoting(false);
  };

  if (loading || allVideos.length === 0) {
    return <div className="loading">Loading videos from database...</div>;
  }

  if (schoolVideos.length === 0) {
    return (
      <div className="year-page">
        <h2>School not found</h2>
        <p>No videos found for "{schoolName}".</p>
        <p>
          <Link to="/">← Back to rankings</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="year-page">
      <h2 style={{ textAlign: "center", marginTop: 0, marginBottom: 4 }}>
        {schoolName}
      </h2>
      <p style={{ textAlign: "center", margin: "0 0 16px", color: "#555" }}>
        {[
          stats.winners > 0 && (
            <span key="w">
              <strong>{stats.winners}</strong> winner
              {stats.winners === 1 ? "" : "s"}
            </span>
          ),
          stats.finalists > 0 && (
            <span key="f">
              <strong>{stats.finalists}</strong> finalist
              {stats.finalists === 1 ? "" : "s"}
            </span>
          ),
          stats.nominees > 0 && (
            <span key="n">
              <strong>{stats.nominees}</strong> nominee
              {stats.nominees === 1 ? "" : "s"}
            </span>
          ),
        ]
          .filter(Boolean)
          .reduce((acc, el, i) => (i === 0 ? [el] : [...acc, " · ", el]), [])}
      </p>
      <div className="year-toggles">
        {TOGGLES.map((t) => (
          <button
            key={t.key}
            className={`year-toggle${
              activeToggle === t.key ? " active" : ""
            }`}
            onClick={() => setActiveToggle(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length < 2 ? (
        <div className="loading">
          Not enough videos from {schoolName} for this category.
        </div>
      ) : (
        <div className="year-layout">
          <div className="year-vote">
            <div className="video-container">
              {leftVideo && rightVideo && (
                <>
                  <VideoBox
                    video={leftVideo}
                    handleVote={() => handleVote(leftVideo.id, rightVideo.id)}
                  />
                  <VideoBox
                    video={rightVideo}
                    handleVote={() => handleVote(rightVideo.id, leftVideo.id)}
                  />
                </>
              )}
            </div>
            <button className="vote-button" onClick={pickPair}>
              Skip
            </button>
          </div>

          <div className="year-rankings">
            <h3>{schoolName} Rankings</h3>
            <div className="year-table-wrap">
              <table className="year-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Div</th>
                    <th>Video</th>
                    <th>Year</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((v, i) => (
                    <tr key={v.id}>
                      <td>{MEDALS[i] || i + 1}</td>
                      <td>{v.division || "?"}</td>
                      <td>
                        <Link to={`/video/${v.id}`}>{v.name || "??"}</Link>{" "}
                        {v.winner ? (
                          <FaStar style={{ color: "gold", fontSize: 14 }} />
                        ) : v.finalist ? (
                          <FaRegStar style={{ color: "grey", fontSize: 12 }} />
                        ) : null}
                      </td>
                      <td>{v.year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolPage;
